import { MouseEventHandler } from 'react';
import * as React from 'react';

import { IconComponent, IconStyle } from 'shared/entities/components/Icon';
import {
  PopupPosition,
  PopupEventType,
  PopupTrigger,
  PopupChildren
} from 'shared/entities/components/Popup';
import {
  TranslationNode,
  TranslationString
} from 'shared/entities/localization';
import { LoadingStage } from 'shared/entities/meta';
import { ComponentType } from 'shared/entities/components/Component';
import { CommonDirection } from 'shared/entities/common/client';
import { SortDirection } from 'shared/newEntities/common';

import { WithMetaProps } from './WithMeta';
import { TagProps, TagSize } from './Tag';
import { ExternalLinkTarget } from './ExternalLink';
import {
  TypographyColor,
  TypographySize,
  TypographyWeight
} from './Typography';
import { AvatarProps } from './user/Avatar';

export type SelectorRightComponentProps = {
  Icon: IconComponent;
  iconStyle?: IconStyle;
  reference?: TranslationString;
  action:
    | {
        type: ComponentType.a;
        href: string;
        onClick?: MouseEventHandler<HTMLAnchorElement>;
      }
    | {
        type: ComponentType.link;
        to: string;
        target?: ExternalLinkTarget;
        onClick?: MouseEventHandler<HTMLAnchorElement>;
      }
    | { type: ComponentType.svg; onClick: MouseEventHandler<SVGElement> };
};

export type SelectorKey = string | number;

export type SelectorOptionSlot = {
  forOption?: React.ReactNode;
  forValue?: React.ReactNode;
};

export type SelectorBaseItem<
  T extends string | TranslationString = TranslationString | string,
  D extends string | TranslationString = TranslationString | string
> = {
  title: T;
  description?: D;
  Icon?: IconComponent;
  avatar?: AvatarProps;
  rightComponent?: SelectorRightComponentProps;
  tagProps?: TagProps;
  disabled?: boolean;
  extraComponent?: SelectorOptionSlot & {
    position?:
      | CommonDirection.left
      | CommonDirection.right
      | CommonDirection.bottom;
  };
  hoverReference?: React.ReactNode;
  disabledReference?: TranslationNode;
};

export type SelectorBaseProps = {
  position?: PopupPosition | PopupPosition[];
  on?: PopupEventType;
  closeOnDocumentClick?: boolean;
  withoutBorder?: boolean;
  // одинаковая ширина Trigger и Content
  sameWidth?: boolean;
  trigger?: PopupTrigger;
  renderTrigger?: (args: { isOpen: boolean }) => React.ReactNode;
  children: PopupChildren;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  contentTopNode?: React.ReactNode;
  className?: string;
  triggerSize?: SelectorTriggerSize;
  size?: SelectorSize;
  triggerClassName?: string;
  isOpen?: boolean;
} & WithMetaProps;

export type SelectorItem<
  T extends string | TranslationString = TranslationString | string,
  D extends string | TranslationString = TranslationString | string
> = SelectorBaseItem<T, D> & {
  id: SelectorKey;
};

export type SelectorEntities<
  K extends SelectorKey = SelectorKey,
  T extends string | TranslationString = TranslationString | string
> = Record<K, SelectorItem<T>>;

export type SelectorOptions = {
  keys: SelectorKey[];
  entities: SelectorEntities;
};

export enum SelectorType {
  single = 'single',
  multi = 'multi'
}

export enum SelectorSize {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

/**
 * Размер Tag в опции Selector, чтобы шрифт тега совпадал со шрифтом title опции
 */
export const mapSelectorSizeToTagSize: Record<SelectorSize, TagSize> = {
  [SelectorSize.small]: TagSize.sm,
  [SelectorSize.medium]: TagSize.md,
  [SelectorSize.large]: TagSize.lg
};

export enum SelectorOptionSize {
  s = 's',
  m = 'm',
  l = 'l'
}

export enum SelectorTriggerSize {
  xs = 'xs',
  s = 's',
  sm = 'sm',
  m = 'm',
  l = 'l',
  xl = 'xl'
}

export { SortDirection as SelectorSortOrder };

export type SelectorTopItem = {
  Icon?: IconComponent;
  title: TranslationString | string;
  onClick: (onClose: () => void) => void;
};

export type SelectorPopupSearchConfig = {
  placeholder?: TranslationString | string;
};

export type SelectorProps = Omit<
  SelectorBaseProps,
  'trigger' | 'children' | 'contentTopNode'
> & {
  options: SelectorOptions;
  placeholder?: TranslationString | string;
  pluralize?: (count: number) => TranslationString;
  loadingStage?: LoadingStage;
  sortOrder?: SortDirection | null;
  title?: TranslationString | string;
  topItem?: SelectorTopItem;
  onRightIconClick?: (key: SelectorKey) => void;
  TriggerIcon?: IconComponent;
  largeOption?: boolean;
  smallDescription?: boolean;
  withoutSearch?: boolean;
  withCroppedTitle?: boolean;
  onBlur?: () => void;
  fontSize?: TypographySize;
  triggerFontWeight?: TypographyWeight;
  triggerFontColor?: TypographyColor;
  hoverReferencePosition?: PopupPosition;
  emptyText?: TranslationString;
  helperText?: TranslationString | null;
  withStub?: boolean;
  stablePopupSize?: boolean;
  popupSearch?: SelectorPopupSearchConfig;
  renderTrigger?: (args: { isOpen: boolean }) => React.ReactNode;
  contentTopNode?: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  withRemovingInTrigger?: boolean;
} & (
    | {
        type: SelectorType.single;
        value: SelectorKey | null;
        withRemoving?: boolean;
        onChange: (value: SelectorKey | null) => void;
      }
    | {
        type: SelectorType.multi;
        value: SelectorKey[];
        onChange: (value: SelectorKey[]) => void;
        saveKeysOrder?: boolean;
        showSelectedAll?: boolean;
        /** Keys in value that should not affect multi trigger label count */
        excludeFromTriggerCountIds?: SelectorKey[];
      }
  );
